import { Card, ErrorMessage, Input } from "@";
import { ReactSortable } from "react-sortablejs";
import { Squares2X2Icon } from "@heroicons/react/24/outline";
import { attributeDurationsOptions } from "../../lib/options";
import type { ProjectAttributesSectionProps } from "./types";
import type { ProjectAttribute } from "../../types";
import { ProjectType } from "../../types";
import {
  RepeaterAddButton,
  RepeaterClearAll,
  RepeaterEmpty,
  RepeaterItem,
} from "../RepeaterPrimitives";
import React from "react";

const defaultAttribute: ProjectAttribute = {
  title: "",
  price: 0,
  order: 0,
  duration: "",
  endingTime: "",
  numberOfBeneficiariesNames: 0,
  verificationStartDate: "",
  verificationEndDate: "",
};

export const ProjectAttributesSection = ({
  register: _register,
  watch,
  setValue,
  errors,
  show,
}: ProjectAttributesSectionProps) => {
  const projectAttributes =
    (watch("projectAttributes") as ProjectAttribute[]) || [];
  const projectType = Number(watch("projectType"));
  const enableVerification = watch("enableVerification") as boolean;

  const addAttribute = () => {
    const newAttribute: ProjectAttribute = {
      ...defaultAttribute,
      order: projectAttributes.length,
    };
    setValue("projectAttributes", [...projectAttributes, newAttribute]);
  };

  const removeAttribute = (index: number) => {
    const updated = projectAttributes
      .filter((_, i) => i !== index)
      .map((item, i) => ({ ...item, order: i }));
    setValue("projectAttributes", updated);
  };

  const updateAttribute = (
    index: number,
    field: keyof ProjectAttribute,
    value: string | number,
  ) => {
    const updated = projectAttributes.map((item, i) =>
      i === index ? { ...item, [field]: value } : item,
    );
    setValue("projectAttributes", updated);
  };

  const handleSort = (sortedItems: ProjectAttribute[]) => {
    const updated = sortedItems.map((item, i) => ({ ...item, order: i }));
    setValue("projectAttributes", updated);
  };

  const gridClassName =
    projectType === ProjectType.Deduction
      ? "grid-cols-2 lg:grid-cols-4"
      : projectType === ProjectType.Meal
        ? "md:grid-cols-3 grid-cols-2"
        : "grid-cols-2 lg:grid-cols-2";

  return (
    <>
      {errors?.projectAttributes && (
        <ErrorMessage value="يوجد خطأ في إعدادات خيارات التبرع" />
      )}
      <Card
        star={!show}
        title={`خيارات التبرع${projectAttributes.length > 0 ? ` (${projectAttributes.length})` : ""}`}
        className="lg:col-span-2"
      >
        {projectAttributes.length === 0 && !show && (
          <RepeaterEmpty
            icon={Squares2X2Icon}
            title="لا توجد خيارات تبرع بعد"
            description="أضف على الأقل خيار تبرع واحد لاستكمال إعداد المشروع"
          />
        )}

        {projectAttributes.length > 0 && (
          <ReactSortable
            list={
              projectAttributes as (ProjectAttribute & {
                id?: number | string;
              })[]
            }
            setList={handleSort}
            handle=".drag-handle"
            animation={200}
            className="mb-5 space-y-4"
          >
            {projectAttributes.map((item, index) => (
              <RepeaterItem
                key={item.id ?? index}
                index={index}
                label={`خيار التبرع ${index + 1}`}
                show={show}
                draggable
                onRemove={() => removeAttribute(index)}
              >
                <div className={`grid gap-4 ${gridClassName}`}>
                  <Input
                    type="text"
                    label="الاسم"
                    disabled={!!show}
                    value={item.title}
                    change={(e: React.ChangeEvent<HTMLInputElement>) =>
                      updateAttribute(index, "title", e.target.value)
                    }
                    error={errors?.projectAttributes?.[index]?.title}
                  />

                  <Input
                    type="number"
                    min="0"
                    label="المبلغ"
                    disabled={!!show}
                    value={item.price}
                    change={(e: React.ChangeEvent<HTMLInputElement>) =>
                      updateAttribute(index, "price", e.target.value)
                    }
                    error={errors?.projectAttributes?.[index]?.price}
                  />

                  {projectType === ProjectType.Meal && (
                    <Input
                      type="number"
                      min="0"
                      label="عدد الملصقات"
                      disabled={!!show}
                      value={item.numberOfBeneficiariesNames}
                      change={(e: React.ChangeEvent<HTMLInputElement>) =>
                        updateAttribute(
                          index,
                          "numberOfBeneficiariesNames",
                          e.target.value,
                        )
                      }
                      error={
                        errors?.projectAttributes?.[index]
                          ?.numberOfBeneficiariesNames
                      }
                    />
                  )}

                  {projectType === ProjectType.Deduction && (
                    <>
                      <Input
                        type="select"
                        list={attributeDurationsOptions}
                        label="فترة الاستقطاع"
                        disabled={!!show}
                        value={item.duration}
                        change={(e: React.ChangeEvent<HTMLSelectElement>) =>
                          updateAttribute(index, "duration", e.target.value)
                        }
                        error={errors?.projectAttributes?.[index]?.duration}
                      />

                      <Input
                        type="datepicker"
                        label="نهاية فترة الاستقطاع"
                        disabled={!!show}
                        value={item.endingTime}
                        change={(e: string) =>
                          updateAttribute(index, "endingTime", e)
                        }
                        error={errors?.projectAttributes?.[index]?.endingTime}
                      />
                    </>
                  )}

                  {enableVerification &&
                    [ProjectType.Meal, ProjectType.Grades].includes(
                      projectType,
                    ) && (
                      <Input
                        star
                        disabled={!!show}
                        type="date-range"
                        style="col-span-2"
                        labelA="تاريخ بداية التوثيق"
                        labelB="تاريخ نهاية التوثيق"
                        valueB={item.verificationEndDate}
                        valueA={item.verificationStartDate}
                        changeA={(e: string) =>
                          updateAttribute(index, "verificationStartDate", e)
                        }
                        changeB={(e: string) =>
                          updateAttribute(index, "verificationEndDate", e)
                        }
                        errorA={
                          errors?.projectAttributes?.[index]
                            ?.verificationStartDate
                        }
                        errorB={
                          errors?.projectAttributes?.[index]
                            ?.verificationEndDate
                        }
                      />
                    )}
                </div>
              </RepeaterItem>
            ))}
          </ReactSortable>
        )}

        {!show && (
          <div className="mt-4 space-y-3">
            <RepeaterAddButton
              onClick={addAttribute}
              label="إضافة خيار تبرع"
              hint="حدد الاسم، المبلغ، وأي إعدادات إضافية حسب نوع المشروع"
            />
            {projectAttributes.length > 1 && (
              <div className="flex justify-end">
                <RepeaterClearAll
                  onClick={() => setValue("projectAttributes", [])}
                  label="حذف جميع الخيارات"
                />
              </div>
            )}
          </div>
        )}

        <ErrorMessage
          value={(errors.projectAttributes as { message?: string })?.message}
        />
      </Card>
    </>
  );
};
